import { useSyncExternalStore } from 'react';
import en from '@/shared/locales/en';

/**
 * Site translations. Keys are the English text itself, so anything not yet translated
 * simply shows in English. English is the default; a visitor's browser language is used
 * when we have it, and a language they pick themselves is remembered on this device.
 */
export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية', rtl: true },
  { code: 'bn', label: 'বাংলা' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'ur', label: 'اردو', rtl: true },
];

export const DEFAULT_LANG = 'en';
const STORAGE_KEY = 'gc-lang';

/** Each language is split into a few files (one per part of the site), loaded together. */
const LOADERS = Object.fromEntries(
  ['zh', 'hi', 'es', 'fr', 'ar', 'bn', 'pt', 'ru', 'ur'].map((code) => [
    code,
    [
      () => import(`./locales/${code}.js`),
      () => import(`./locales/home/${code}.js`),
      () => import(`./locales/pages/${code}.js`),
      () => import(`./locales/install/${code}.js`),
      () => import(`./locales/checkout/${code}.js`),
      () => import(`./locales/account/${code}.js`),
      () => import(`./locales/account2/${code}.js`),
    ],
  ]),
);

const isSupported = (code) => LANGUAGES.some((l) => l.code === code);

function readSaved() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isSupported(v) ? v : null;
  } catch {
    return null;
  }
}

/** First supported language in the browser's list, matched on its base code (pt-BR → pt). */
function detectBrowser() {
  if (typeof navigator === 'undefined') return DEFAULT_LANG;
  const list = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of list) {
    const base = String(tag || '').toLowerCase().split('-')[0];
    if (isSupported(base)) return base;
  }
  return DEFAULT_LANG;
}

let state = { lang: DEFAULT_LANG, dict: en };
const listeners = new Set();
const cache = { en };

function emit() {
  listeners.forEach((fn) => fn());
}

function applyDocument(lang) {
  if (typeof document === 'undefined') return;
  const meta = LANGUAGES.find((l) => l.code === lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr';
}

async function load(lang) {
  if (cache[lang]) return cache[lang];
  const mods = await Promise.all(LOADERS[lang].map((fn) => fn()));
  cache[lang] = Object.assign({}, en, ...mods.map((m) => m.default));
  return cache[lang];
}

/** Switch language. `remember` is true when the visitor picked it themselves. */
export async function setLanguage(lang, remember = true) {
  const code = isSupported(lang) ? lang : DEFAULT_LANG;
  if (remember) {
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Private mode: the choice lasts for this visit only.
    }
  }
  try {
    const dict = await load(code);
    state = { lang: code, dict };
  } catch {
    state = { lang: DEFAULT_LANG, dict: en };
  }
  applyDocument(state.lang);
  emit();
}

// Start in the saved or browser language as soon as the app loads.
if (typeof window !== 'undefined') {
  const start = readSaved() ?? detectBrowser();
  if (start !== DEFAULT_LANG) setLanguage(start, false);
  else applyDocument(DEFAULT_LANG);
}

function pick(value, lang, count) {
  if (value && typeof value === 'object') {
    let form = 'other';
    try {
      form = new Intl.PluralRules(lang).select(Number(count ?? 0));
    } catch {
      // Fall back to the general form.
    }
    return value[form] ?? value.other ?? value.one ?? '';
  }
  return value;
}

/** Translate an English string. Use {name} placeholders; pass `count` for plurals. */
export function translate(text, vars, s = state) {
  const raw = s.dict[text] ?? en[text] ?? text;
  const out = pick(raw, s.lang, vars?.count);
  if (!vars) return out;
  return String(out).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const getSnapshot = () => state;

/** Current language and a `t` bound to it. Components re-render when the language changes. */
export function useT() {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const t = (text, vars) => translate(text, vars, s);
  const meta = LANGUAGES.find((l) => l.code === s.lang) ?? LANGUAGES[0];
  return { t, lang: s.lang, rtl: !!meta.rtl, setLanguage };
}

/** Current language code outside React (for emails and other non-component code). */
export const currentLanguage = () => state.lang;
